import {
    CLEAR_VCARD,
    ClearVCard,
    CLOSE_VCARD_EDITOR,
    CloseVCardEditor,
    OPEN_VCARD_EDITOR,
    OpenVCardEditor,
    REMOVE_VCARD_ERROR,
    RemoveVCardErrors,
    RESET_VCARD,
    ResetVCard,
    SAVE_SUCCESS_FOR_EDITOR_VCARDS,
    SAVE_VCARDS,
    SaveSuccessForEditorVCards,
    SaveVCards,
    TOGGLE_VCARD_NOT_SHOW_METRO_IN_SEARCH,
    TOGGLE_VCARD_SHORT_VERSION_FLAG,
    ToggleVCardNotShowMetroInSearchFlag,
    ToggleVCardShortVersionFlag,
    UPDATE_DRAFT_VCARD,
    UPDATE_VCARD_METRO_STATION_INFO,
    UPDATE_VCARDS_ERRORS,
    UPDATE_VISITING_CARD_ERRORS,
    UpdateDraftVCard,
    UpdateVCardMetroStationInfo,
    UpdateVCardsErrors,
    UpdateVisitingCardsErrors,
    VisitingCardEditorAction
} from '../actions/visitingCardEditor';
import { VcardStatus } from '../typings/banners';
import { State } from '../typings/state';
import { VisitingCardEditorsState } from '../typings/VisitingCardEditor';

const openVcardEditor = (
    state: VisitingCardEditorsState,
    action: OpenVCardEditor
): VisitingCardEditorsState => {
    const {
        savedValue: { info, ...otherParamsSavedValue },
        editorId,
        vCardId
    } = action;

    return {
        ...state,
        [editorId]: {
            ...state[editorId],
            ...otherParamsSavedValue,
            vCardStatus: VcardStatus.EDITING,
            vCardData: info,
            isOpen: true,
            vCardId
        }
    };
};

const closeVcardEditor = (
    state: VisitingCardEditorsState,
    action: CloseVCardEditor
): VisitingCardEditorsState => {
    const { editorId } = action;

    return {
        ...state,
        [editorId]: {
            ...state[editorId],
            isOpen: false
        }
    };
};

const toggleVcardShortVersionFlag = (
    state: VisitingCardEditorsState,
    action: ToggleVCardShortVersionFlag
): VisitingCardEditorsState => {
    const { editorId } = action;
    const editor = state[editorId];

    return {
        ...state,
        [editorId]: {
            ...editor,
            isShortVersionChecked: !editor.isShortVersionChecked
        }
    };
};

const toggleVcardNotShowMetroInSearch = (
    state: VisitingCardEditorsState,
    action: ToggleVCardNotShowMetroInSearchFlag
): VisitingCardEditorsState => {
    const { editorId } = action;
    const editor = state[editorId];

    return {
        ...state,
        [editorId]: {
            ...editor,
            isNotShowMetroInSearchChecked: !editor.isNotShowMetroInSearchChecked
        }
    };
};

const updateDraftVcard = (
    state: VisitingCardEditorsState,
    action: UpdateDraftVCard
): VisitingCardEditorsState => {
    const { editorId, vCard, vCardStatus = VcardStatus.EDITING } = action;
    const editor = state[editorId];

    return {
        ...state,
        [editorId]: {
            ...editor,
            vCardData: vCard,
            vCardStatus: vCardStatus
        }
    };
};

const resetVcard = (
    state: VisitingCardEditorsState,
    action: ResetVCard
): VisitingCardEditorsState => {
    const {
        savedValue: { info, ...otherParamsSavedValue },
        editorId
    } = action;

    return {
        ...state,
        [editorId]: {
            ...state[editorId],
            ...otherParamsSavedValue,
            vCardData: info,
            isOpen: false,
            errors: undefined
        }
    };
};

const saveVcards = (
    state: VisitingCardEditorsState,
    action: SaveVCards
): VisitingCardEditorsState => {
    const { editorId } = action;

    return {
        ...state,
        [editorId]: {
            ...state[editorId],
            vCardStatus: VcardStatus.PROGRESS
        }
    };
};

const updateVcardsErrors = (
    state: VisitingCardEditorsState,
    action: UpdateVCardsErrors
): VisitingCardEditorsState => {
    const { editorIds, updateEditorIds, errors: errorsData } = action;

    return {
        ...state,
        ...editorIds.reduce((acc, editorId, index) => {
            if (updateEditorIds.includes(editorId)) {
                acc[editorId] = {
                    ...state[editorId],
                    errors: errorsData[index],
                    vCardStatus: VcardStatus.EDITING
                };
            } else {
                acc[editorId] = {
                    ...state[editorId],
                    isOpen: false,
                    vCardStatus: VcardStatus.EDITING
                };
            }

            return acc;
        }, {})
    };
};

const saveSuccessForEditorVCards = (
    state: VisitingCardEditorsState,
    action: SaveSuccessForEditorVCards
): VisitingCardEditorsState => {
    const { editorIds, vCardIds } = action;

    return {
        ...state,
        ...editorIds.reduce((acc, editorId, index) => {
            acc[editorId] = {
                ...state[editorId],
                isOpen: false,
                vCardStatus: VcardStatus.EDITING,
                errors: undefined,
                vCardId: vCardIds[index]
            };

            return acc;
        }, {})
    };
};

const clearVCard = (
    state: VisitingCardEditorsState,
    action: ClearVCard
): VisitingCardEditorsState => {
    const { editorIds } = action;

    return {
        ...state,
        ...editorIds.reduce((acc, editorId) => {
            acc[editorId] = {
                ...state[editorId],
                isOpen: false,
                vCardStatus: VcardStatus.EDITING,
                vCardId: '',
                errors: undefined
            };

            return acc;
        }, {})
    };
};

const removeVcardError = (
    state: VisitingCardEditorsState,
    action: RemoveVCardErrors
): VisitingCardEditorsState => {
    const { editorId, paths } = action;
    const errors = state[editorId].errors;

    if (!errors) {
        return state;
    }

    return {
        ...state,
        [editorId]: {
            ...state[editorId],
            errors: errors.filter(
                error => !error.path || !paths.includes(error.path)
            )
        }
    };
};

const updateVCardMetroStationInfo = (
    state: VisitingCardEditorsState,
    action: UpdateVCardMetroStationInfo
): VisitingCardEditorsState => {
    const { editorId, metroStationId, metroStationName } = action;
    const vCardData = state[editorId].vCardData;

    if (!vCardData) {
        return state;
    }

    return {
        ...state,
        [editorId]: {
            ...state[editorId],
            vCardData: {
                ...vCardData,
                metroStationId,
                metroStationName
            }
        }
    };
};

const updateVisitingCardErrors = (
    state: VisitingCardEditorsState,
    action: UpdateVisitingCardsErrors
): VisitingCardEditorsState => {
    const { editorId, errors } = action;

    return {
        ...state,
        [editorId]: {
            ...state[editorId],
            errors
        }
    };
};

export function visitingCardEditor(
    state: VisitingCardEditorsState = {},
    action: VisitingCardEditorAction,
    fullState: State
): VisitingCardEditorsState {
    switch (action.type) {
        case OPEN_VCARD_EDITOR:
            return openVcardEditor(state, action);
        case CLOSE_VCARD_EDITOR:
            return closeVcardEditor(state, action);
        case TOGGLE_VCARD_SHORT_VERSION_FLAG:
            return toggleVcardShortVersionFlag(state, action);
        case TOGGLE_VCARD_NOT_SHOW_METRO_IN_SEARCH:
            return toggleVcardNotShowMetroInSearch(state, action);
        case UPDATE_DRAFT_VCARD:
            return updateDraftVcard(state, action);
        case RESET_VCARD:
            return resetVcard(state, action);
        case SAVE_VCARDS:
            return saveVcards(state, action);
        case UPDATE_VCARDS_ERRORS:
            return updateVcardsErrors(state, action);
        case SAVE_SUCCESS_FOR_EDITOR_VCARDS:
            return saveSuccessForEditorVCards(state, action);
        case CLEAR_VCARD:
            return clearVCard(state, action);
        case UPDATE_VCARD_METRO_STATION_INFO:
            return updateVCardMetroStationInfo(state, action);
        case REMOVE_VCARD_ERROR:
            return removeVcardError(state, action);
        case UPDATE_VISITING_CARD_ERRORS:
            return updateVisitingCardErrors(state, action);
        default:
            return state;
    }
}
